import { BASE_ADMIN_API, fetchAPI } from '@/services/api';

/** Currency master row from admin. */
export interface AdminCurrencyVO {
    currencyCode?: string;
    symbol?: string;
    englishName?: string;
    chineseName?: string;
    country?: string;
    hasHkmaRate?: number;
    isPopular?: number;
    popularRank?: number;
    isBase?: number;
}

/** FX query result: 1 unit = rateToHkd HKD. */
export interface AdminCurrencyRateVO {
    currencyCode?: string;
    rateDate?: string;
    rateToHkd?: number | string;
    source?: string;
    sourceDate?: string;
}

/** One calendar day of FX vs HKD (wide row). */
export interface AdminCurrencyRateDailyVO {
    id?: number;
    rateDate?: string;
    rates?: Record<string, number | string>;
    source?: string;
    sourceDate?: string;
}

/** MyBatis-Plus Page for daily rates. */
export interface CurrencyRateDailyPage {
    records: AdminCurrencyRateDailyVO[];
    total: number;
    size: number;
    current: number;
    pages: number;
}

/** Page query params for daily rates. */
export interface CurrencyRatePageQuery {
    pageNum?: number;
    pageSize?: number;
    currencyCode?: string;
    rateDate?: string;
}

const currencyBase = `${BASE_ADMIN_API}/currency`;
const rateBase = `${BASE_ADMIN_API}/currency-rate`;

/** List currencies; set popularOnly for Popular section. */
export async function listCurrencies(popularOnly = false) {
    return fetchAPI<AdminCurrencyVO[]>(`${currencyBase}/list?popularOnly=${popularOnly}`, {
        method: 'GET',
    });
}

/** Popular currencies ordered by popular_rank. */
export async function listPopularCurrencies() {
    return fetchAPI<AdminCurrencyVO[]>(`${currencyBase}/popular`, { method: 'GET' });
}

/**
 * Query FX vs HKD.
 * @param currencyCode ISO code
 * @param rateDate DD/MM/YYYY or yyyy-MM-dd
 */
export async function queryCurrencyRate(currencyCode: string, rateDate: string) {
    const q = new URLSearchParams({ currencyCode, rateDate });
    return fetchAPI<AdminCurrencyRateVO>(`${rateBase}/query?${q.toString()}`, { method: 'GET' });
}

/**
 * Page daily wide rows (one day = one record).
 * Empty filters: rateDate <= today, desc, pageSize default 20.
 */
export async function pageCurrencyRates(query: CurrencyRatePageQuery = {}) {
    const q = new URLSearchParams({
        pageNum: String(query.pageNum ?? 1),
        pageSize: String(query.pageSize ?? 20),
    });
    if (query.currencyCode) q.set('currencyCode', query.currencyCode);
    if (query.rateDate) q.set('rateDate', query.rateDate);
    return fetchAPI<CurrencyRateDailyPage>(`${rateBase}/page?${q.toString()}`, { method: 'GET' });
}

/** Manual HKMA sync (ops). */
export async function syncCurrencyRates() {
    return fetchAPI<{ rateDate?: string }>(`${rateBase}/sync`, { method: 'POST' });
}

export const CurrencyRateService = {
    listCurrencies,
    listPopularCurrencies,
    queryCurrencyRate,
    pageCurrencyRates,
    syncCurrencyRates,
};
